import { type ICommonLinkProps } from '@frontend/pole-ui/lib/components/CommonLink'
import { getLinkFormatOption } from '@/components/composed/landingBlocks/LandingFAQ/helpers'
import type { TLandingFAQItem } from '@/components/composed/landingBlocks/LandingFAQ/types'
import type { TLandingStep } from '@/components/composed/landingBlocks/LandingSteps/types'
import type { IBreadcrumb } from '@/components/ui/Breadcrumbs/Breadcrumb/interfaces'
import { financingPageBreadcrumb } from '@/components/ui/Breadcrumbs/constants'
import { MFORequiredDocuments } from '@/pages/MFOPage/components/MFORequiredDocuments'
import { EFinancingOfferRoute } from '@/types'
import { formatLocalizedString, type TFormatOptions } from '@/utils/formatUtils'
import type { TMFOBenefit } from './components/MFOBenefits/types'
import type { TMFOLoanTerm } from './components/MFOLoanTerms/types'
import { TextWithConsultAction } from './components/TextWithConsultAction'
import { messages } from './messages'

export const BREADCRUMB: IBreadcrumb = {
  caption: messages.breadcrumbs.currentPage,
  link: EFinancingOfferRoute.MFOPage,
}

export const PARTNER_IMAGES = ['pole_rf']
export const ROOT_BREADCRUMBS = [financingPageBreadcrumb]

export enum EMFOPageAnchor {
  ANNOTATION2 = 'annotation2',
  REQUIRED_DOCUMENTS = 'requiredDocuments',
}

export const MFO_PAGE_LINK_FORMAT_OPTIONS: Omit<ICommonLinkProps, 'url'> = {
  className: 'color_green underline underline_green',
}

export const LANDING_STEPS: TLandingStep[] = [
  {
    title: messages.steps[1].title,
  },
  {
    title: messages.steps[2].title,
  },
  {
    title: messages.steps[3].title,
  },
  {
    title: messages.steps[4].title,
  },
]

export const LANDING_BENEFITS: TMFOBenefit[] = [
  {
    id: 'simpleProcedure',
    description: messages.benefits.items.simpleProcedure,
    image: 'financingOffer/mfo/benefits/simpleProcedure.png',
  },
  {
    id: 'suitedFor',
    description: messages.benefits.items.suitedFor,
    image: 'financingOffer/mfo/benefits/suitedFor.png',
  },
  {
    id: 'earlyRepayment',
    description: messages.benefits.items.earlyRepayment,
    image: 'financingOffer/mfo/benefits/earlyRepayment.png',
  },
  {
    id: 'replaymentAtEndOfTerm',
    description: messages.benefits.items.replaymentAtEndOfTerm,
    image: 'financingOffer/mfo/benefits/replaymentAtEndOfTerm.png',
  },
  {
    id: 'probabilityOfApproval',
    description: formatLocalizedString(messages.benefits.items.probabilityOfApproval, {
      AnnotationLink2: getLinkFormatOption(`#${EMFOPageAnchor.ANNOTATION2}`, {
        ...MFO_PAGE_LINK_FORMAT_OPTIONS,
        isLocalAnchor: true,
      }),
    }),
    image: 'financingOffer/mfo/benefits/probabilityOfApproval.png',
  },
  {
    id: 'transferToAnyBank',
    description: messages.benefits.items.transferToAnyBank,
    image: 'financingOffer/mfo/benefits/transferToAnyBank.png',
  },
]

export const LANDING_LOAN_TERMS: TMFOLoanTerm[] = [
  messages.terms.items[1],
  messages.terms.items[2],
  messages.terms.items[3],
  messages.terms.items[4],
  messages.terms.items[5],
  messages.terms.items[6],
  messages.terms.items[7],
]

export const LANDING_FAQ: TLandingFAQItem[] = [
  {
    id: EMFOPageAnchor.REQUIRED_DOCUMENTS,
    question: messages.faq.questions.requiredDocuments.title,
    answer: messages.faq.questions.requiredDocuments.answer,
  },
  {
    question: messages.faq.questions.useLoanInParts.title,
    answer: messages.faq.questions.useLoanInParts.answer,
  },
  {
    question: messages.faq.questions.earlyRepayment.title,
    answer: messages.faq.questions.earlyRepayment.answer,
  },
  {
    question: messages.faq.questions.collateralRequired.title,
    answer: messages.faq.questions.collateralRequired.answer,
  },
  {
    question: messages.faq.questions.landCollateral.title,
    answer: messages.faq.questions.landCollateral.answer,
  },
  {
    question: messages.faq.questions.collateralValuationProcedure.title,
    answer: messages.faq.questions.collateralValuationProcedure.answer,
  },
  {
    question: messages.faq.questions.alreadyHaveLoan.title,
    answer: messages.faq.questions.alreadyHaveLoan.answer,
  },
  {
    question: messages.faq.questions.contacts.title,
    answer: messages.faq.questions.contacts.answer,
  },
]

export const FAQ_FORMAT_OPTIONS: Readonly<TFormatOptions> = {
  OpenConsultingForm: TextWithConsultAction,
  RequiredDocuments: MFORequiredDocuments,
}

export const CLICKED_ON_SUBMIT_IN_PRE_APPROVAL_POPUP_SESSION_STORAGE_KEY = 'clicked-on-submit-in-pre-approval-popup'
export const CLICKED_ON_SUBMIT_IN_PRE_APPROVAL_POPUP_SESSION_STORAGE_VALUE = 'true'
export const CLICKED_ON_SUBMIT_IN_REGION_COVERAGE_MAP_SESSION_STORAGE_KEY = 'clicked-on-submit-in-region-coverage-map'
export const CLICKED_ON_SUBMIT_IN_REGION_COVERAGE_MAP_SESSION_STORAGE_VALUE = 'true'
